import React, { useState, useEffect, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import DashboardHeader from '../components/common/DashboardHeader';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Button,
  Drawer,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Autocomplete,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BadgeIcon from '@mui/icons-material/Badge';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FinanceTaxRulesBoard from '../components/finance/FinanceTaxRulesBoard';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getConfigurationAddButtonSx,
  getConfigurationCardGridSpacing,
  getConfigurationEmbeddedIconBoxSx,
  getConfigurationEmbeddedSectionHeaderSx,
  getConfigurationEmbeddedSectionLeadSx,
  getConfigurationEmbeddedSectionSubtitleSx,
  getConfigurationEmbeddedSectionTitleSx,
  getConfigurationFinanceCardSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../utils/drawerOverlayProps';
import axios from 'axios';
import { setAutoGstEnabledInContext } from '../utils/autoGst';
// Get CSRF token from cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const TAX_TYPE_OPTIONS = [
  { value: 'gst', label: 'GST' },
  { value: 'cgst', label: 'CGST (Central Goods and Services Tax)' },
  { value: 'sgst', label: 'SGST (State Goods and Services Tax)' },
  { value: 'vat', label: 'VAT' },
  { value: 'service_tax', label: 'Service Tax' },
  { value: 'luxury_tax', label: 'Luxury Tax' },
  { value: 'city_tax', label: 'City Tax' },
  { value: 'tourism_tax', label: 'Tourism Tax' },
  { value: 'other', label: 'Other' },
];

const APPLICABLE_ON_OPTIONS = [
  { value: 'room_rate', label: 'Room Rate' },
  { value: 'hall_rate', label: 'Hall Rate' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'services', label: 'Services' },
  { value: 'total_amount', label: 'Total Amount' },
  { value: 'other', label: 'Other' },
];

// Comprehensive currency list (ISO 4217 codes)
const CURRENCY_OPTIONS = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا' },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'ج.م' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs' },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: '₨' },
  { code: 'MMK', name: 'Myanmar Kyat', symbol: 'K' },
  { code: 'KHR', name: 'Cambodian Riel', symbol: '៛' },
  { code: 'LAK', name: 'Lao Kip', symbol: '₭' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'ARS', name: 'Argentine Peso', symbol: '$' },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$' },
  { code: 'COP', name: 'Colombian Peso', symbol: '$' },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/' },
  { code: 'VES', name: 'Venezuelan Bolívar', symbol: 'Bs.' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.' },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'د.ت' },
  { code: 'FJD', name: 'Fijian Dollar', symbol: 'FJ$' },
  { code: 'PGK', name: 'Papua New Guinean Kina', symbol: 'K' },
];

const FINANCE_ACCENT = '#4ce1b6';

function getIdentityFieldSx(isDark) {
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      letterSpacing: '0.04em',
      fontWeight: 700,
      backgroundColor: isDark ? alpha('#fff', 0.03) : alpha('#0f172a', 0.02),
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(FINANCE_ACCENT, 0.45),
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 1.5,
        borderColor: FINANCE_ACCENT,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: FINANCE_ACCENT },
  };
}

function getIdentitySaveButtonSx() {
  return {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 700,
    px: 2.25,
    background: `linear-gradient(90deg, ${FINANCE_ACCENT} 0%, #2dd4a0 100%)`,
    color: '#10231c',
    boxShadow: 'none',
    '&:hover': {
      background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
      boxShadow: `0 8px 18px ${alpha(FINANCE_ACCENT, 0.28)}`,
    },
  };
}

function IdentityStatusChip({ saved, isDark }) {
  return (
    <Chip
      size="small"
      label={saved ? 'Saved' : 'Not set'}
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: '0.7rem',
        color: saved
          ? (isDark ? '#86efac' : '#166534')
          : (isDark ? alpha('#fff', 0.62) : '#64748b'),
        backgroundColor: saved
          ? alpha('#22c55e', isDark ? 0.16 : 0.1)
          : (isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.05)),
      }}
    />
  );
}

const FinanceSetupPage = ({ showHeader = true }) => {
  const isMobileLayout = isConfigurationMobileLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [taxRules, setTaxRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openTaxDrawer, setOpenTaxDrawer] = useState(false);
  const [editingTaxRule, setEditingTaxRule] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taxRuleToDelete, setTaxRuleToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [taxFormData, setTaxFormData] = useState({
    name: '',
    tax_type: 'gst',
    applicable_on: 'room_rate',
    tax_percentage: '',
    tax_amount: '',
    min_amount: '',
    max_amount: '',
    currency: 'INR',
    description: '',
    is_active: true,
    sort_order: 0,
  });
  const [taxErrors, setTaxErrors] = useState({});
  const [taxRuleSaving, setTaxRuleSaving] = useState(false);
  const [gstNumber, setGstNumber] = useState('');
  const [gstLoading, setGstLoading] = useState(false);
  const [gstSaving, setGstSaving] = useState(false);
  const [panNumber, setPanNumber] = useState('');
  const [panSaving, setPanSaving] = useState(false);
  const [autoGstEnabled, setAutoGstEnabled] = useState(false);
  const [autoGstSaving, setAutoGstSaving] = useState(false);

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchTaxRules();
  }, []);

  useEffect(() => {
    fetchGstin();
  }, []);

  const isDark = currentTheme === 'dark';
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff'),
    [isDark]
  );

  const fetchTaxRules = async () => {
    setLoading(true);
    try {
      const csrftoken = getCookie('csrftoken');
      const response = await axios.get('/configuration/api/tax-rules/', {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        },
      });
      if (response.data && response.data.tax_rules) {
        setTaxRules(response.data.tax_rules);
      } else {
        setTaxRules([]);
      }
    } catch (error) {setTaxRules([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGstin = async () => {
    setGstLoading(true);
    try {
      const csrftoken = getCookie('csrftoken');
      const response = await axios.get('/configuration/api/hotel-gstin/', {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        },
      });
      setGstNumber(response.data?.gstin ?? '');
      setPanNumber(response.data?.pan ?? '');
      const enabled = Boolean(response.data?.auto_gst_enabled);
      setAutoGstEnabled(enabled);
      setAutoGstEnabledInContext(enabled);
    } catch (_) {
      setGstNumber('');
    } finally {
      setGstLoading(false);
    }
  };

  const saveGstin = async () => {
    setGstSaving(true);
    try {
      const csrftoken = getCookie('csrftoken');
      await axios.put(
        '/configuration/api/hotel-gstin/',
        { gstin: gstNumber.trim() || null, pan: panNumber.trim() || null },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            ...(csrftoken && { 'X-CSRFToken': csrftoken }),
          },
        }
      );
      setSnackbar({
        open: true,
        message: 'GST number saved successfully',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save GST number.',
        severity: 'error',
      });
    } finally {
      setGstSaving(false);
    }
  };

  const savePan = async () => {
    setPanSaving(true);
    try {
      const csrftoken = getCookie('csrftoken');
      await axios.put(
        '/configuration/api/hotel-gstin/',
        { gstin: gstNumber.trim() || null, pan: panNumber.trim() || null },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            ...(csrftoken && { 'X-CSRFToken': csrftoken }),
          },
        }
      );
      setSnackbar({
        open: true,
        message: 'PAN number saved successfully',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save PAN number.',
        severity: 'error',
      });
    } finally {
      setPanSaving(false);
    }
  };

  const saveAutoGstEnabled = async (enabled) => {
    setAutoGstEnabled(enabled);
    setAutoGstSaving(true);
    try {
      const csrftoken = getCookie('csrftoken');
      await axios.put(
        '/configuration/api/hotel-gstin/',
        { auto_gst_enabled: enabled },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            ...(csrftoken && { 'X-CSRFToken': csrftoken }),
          },
        }
      );
      setAutoGstEnabledInContext(enabled);
      setSnackbar({
        open: true,
        message: enabled
          ? 'Auto GST enabled. Walk-in, Reservation, and Quick Booking will always apply GST.'
          : 'Auto GST disabled. Staff can turn GST on or off on booking review.',
        severity: 'success',
      });
    } catch (error) {
      setAutoGstEnabled(!enabled);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update Auto GST.',
        severity: 'error',
      });
    } finally {
      setAutoGstSaving(false);
    }
  };

  const handleOpenTaxDrawer = (taxRule = null, defaults = {}) => {
    if (taxRule) {
      setEditingTaxRule(taxRule);
      setTaxFormData({
        name: taxRule.name,
        tax_type: taxRule.tax_type,
        applicable_on: taxRule.applicable_on,
        tax_percentage: taxRule.tax_percentage || '',
        tax_amount: taxRule.tax_amount || '',
        min_amount: taxRule.min_amount || '',
        max_amount: taxRule.max_amount || '',
        currency: taxRule.currency,
        description: taxRule.description || '',
        is_active: taxRule.is_active,
        sort_order: taxRule.sort_order,
      });
    } else {
      setEditingTaxRule(null);
      setTaxFormData({
        name: '',
        tax_type: 'gst',
        applicable_on: defaults.applicable_on || 'room_rate',
        tax_percentage: '',
        tax_amount: '',
        min_amount: '',
        max_amount: '',
        currency: 'INR',
        description: '',
        is_active: true,
        sort_order: 0,
      });
    }
    setTaxErrors({});
    setOpenTaxDrawer(true);
  };

  const handleCloseTaxDrawer = () => {
    setOpenTaxDrawer(false);
    setEditingTaxRule(null);
    setTaxErrors({});
  };

  const handleTaxInputChange = (field, value) => {
    setTaxFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (taxErrors[field]) {
      setTaxErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateTaxForm = () => {
    const newErrors = {};

    if (!taxFormData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!taxFormData.tax_percentage && !taxFormData.tax_amount) {
      newErrors.tax_percentage = 'Either tax percentage or tax amount must be specified';
    }

    if (taxFormData.tax_percentage && taxFormData.tax_amount) {
      newErrors.tax_percentage = 'Cannot set both tax percentage and tax amount';
    }

    if (taxFormData.tax_percentage) {
      const percentage = parseFloat(taxFormData.tax_percentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        newErrors.tax_percentage = 'Tax percentage must be between 0 and 100';
      }
    }

    if (taxFormData.tax_amount) {
      const amount = parseFloat(taxFormData.tax_amount);
      if (isNaN(amount) || amount < 0) {
        newErrors.tax_amount = 'Tax amount cannot be negative';
      }
    }

    if (taxFormData.min_amount && taxFormData.max_amount) {
      const min = parseFloat(taxFormData.min_amount);
      const max = parseFloat(taxFormData.max_amount);
      if (!isNaN(min) && !isNaN(max) && min > max) {
        newErrors.min_amount = 'Minimum amount cannot be greater than maximum amount';
      }
    }

    setTaxErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveTaxRule = async () => {
    if (!validateTaxForm()) {
      return;
    }

    setTaxRuleSaving(true);
    try {
      const url = editingTaxRule
        ? `/configuration/api/tax-rules/${editingTaxRule.id}/`
        : '/configuration/api/tax-rules/';

      const method = editingTaxRule ? 'put' : 'post';
      const csrftoken = getCookie('csrftoken');

      const response = await axios[method](url, taxFormData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        },
      });

      if (response.data.success || response.data.tax_rule) {
        setSnackbar({
          open: true,
          message: editingTaxRule ? 'Tax rule updated successfully' : 'Tax rule created successfully',
          severity: 'success',
        });
        handleCloseTaxDrawer();
        fetchTaxRules();
      } else if (response.data.errors) {
        setTaxErrors(response.data.errors);
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setTaxErrors(error.response.data.errors);
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || 'Failed to save tax rule. Please try again.',
          severity: 'error',
        });
      }
    } finally {
      setTaxRuleSaving(false);
    }
  };

  const handleDeleteTaxRule = (taxRule) => {
    setTaxRuleToDelete(taxRule);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTaxConfirm = async () => {
    if (!taxRuleToDelete) return;

    setDeletingId(taxRuleToDelete.id);
    try {
      const csrftoken = getCookie('csrftoken');
      await axios.delete(`/configuration/api/tax-rules/${taxRuleToDelete.id}/`, {
        withCredentials: true,
        headers: {
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        },
      });
      setSnackbar({
        open: true,
        message: 'Tax rule deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setTaxRuleToDelete(null);
      fetchTaxRules();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete tax rule. Please try again.',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box 
      sx={{ 
        p: showHeader ? 3 : 0,
        backgroundColor: showHeader ? (isDark ? '#12151c' : '#f2f4f7') : 'transparent',
        minHeight: showHeader ? '100vh' : 'auto',
        transition: 'background-color 0.3s ease',
      }}
    >
      {showHeader && (
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Finance Setup"
            navLinks={[
              { url: '/configuration/', label: 'Configuration' },
              { url: '/finance/', label: 'Finance Dashboard' },
            ]}
          />
        </Box>
      )}

      <Grid container spacing={getConfigurationCardGridSpacing(isMobileLayout)}>
        {/* Hotel GST Number */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              ...getConfigurationFinanceCardSx(isDark, isMobileLayout),
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} sx={{ mb: 2.25 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={getConfigurationEmbeddedIconBoxSx(FINANCE_ACCENT, isMobileLayout)}>
                  <AccountBalanceIcon sx={{ fontSize: 26, color: FINANCE_ACCENT }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: isDark ? '#f8fafc' : '#0f172a' }}>
                    Hotel GST Number
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: isDark ? alpha('#fff', 0.5) : '#64748b' }}>
                    GSTIN printed on tax invoices
                  </Typography>
                </Box>
              </Stack>
              <IdentityStatusChip saved={Boolean(gstNumber.trim())} isDark={isDark} />
            </Stack>
            {gstLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} sx={{ color: FINANCE_ACCENT }} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            ) : (
              <Stack spacing={2} sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="GST Number (GSTIN)"
                  placeholder="e.g. 27AABCU9603R1ZM"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                  disabled={gstSaving}
                  sx={getIdentityFieldSx(isDark)}
                  inputProps={{ maxLength: 20 }}
                  helperText="15-character GSTIN for invoices and receipts."
                  FormHelperTextProps={{
                    sx: { mt: 0.75, color: isDark ? alpha('#fff', 0.45) : '#64748b' },
                  }}
                />
                <Box
                  sx={{
                    mt: 'auto',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.03),
                    border: `1px solid ${isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.06)}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: isDark ? '#e2e8f0' : '#0f172a' }}>
                      Auto GST Enabled
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: isDark ? alpha('#fff', 0.48) : '#64748b' }}>
                      Always apply GST on Walk-in, Reservation, and Quick Booking. Review toggle stays hidden.
                    </Typography>
                  </Box>
                  <Switch
                    checked={autoGstEnabled}
                    onChange={(e) => saveAutoGstEnabled(e.target.checked)}
                    disabled={autoGstSaving}
                    sx={{
                      flexShrink: 0,
                      '& .MuiSwitch-switchBase.Mui-checked': { color: FINANCE_ACCENT },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: FINANCE_ACCENT,
                      },
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={saveGstin}
                  disabled={gstSaving}
                  sx={{ ...getIdentitySaveButtonSx(), alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  {gstSaving ? <CircularProgress size={20} color="inherit" /> : 'Save GST Number'}
                </Button>
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Hotel PAN Number */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              ...getConfigurationFinanceCardSx(isDark, isMobileLayout),
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} sx={{ mb: 2.25 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={getConfigurationEmbeddedIconBoxSx(FINANCE_ACCENT, isMobileLayout)}>
                  <BadgeIcon sx={{ fontSize: 26, color: FINANCE_ACCENT }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: isDark ? '#f8fafc' : '#0f172a' }}>
                    Hotel PAN Number
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: isDark ? alpha('#fff', 0.5) : '#64748b' }}>
                    PAN for tax and compliance
                  </Typography>
                </Box>
              </Stack>
              <IdentityStatusChip saved={Boolean(panNumber.trim())} isDark={isDark} />
            </Stack>
            {gstLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} sx={{ color: FINANCE_ACCENT }} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            ) : (
              <Stack spacing={2} sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="PAN Number"
                  placeholder="e.g. AABCU9603R"
                  value={panNumber}
                  onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                  disabled={panSaving}
                  sx={getIdentityFieldSx(isDark)}
                  inputProps={{ maxLength: 20 }}
                  helperText="Used on invoices and tax compliance documents."
                  FormHelperTextProps={{
                    sx: { mt: 0.75, color: isDark ? alpha('#fff', 0.45) : '#64748b' },
                  }}
                />
                <Box
                  sx={{
                    mt: 'auto',
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.03),
                    border: `1px solid ${isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.06)}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: isDark ? '#e2e8f0' : '#0f172a' }}>
                    Invoice identity
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: isDark ? alpha('#fff', 0.48) : '#64748b' }}>
                    Keep PAN aligned with the legal hotel name shown on tax invoices.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={savePan}
                  disabled={panSaving}
                  sx={{ ...getIdentitySaveButtonSx(), alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  {panSaving ? <CircularProgress size={20} color="inherit" /> : 'Save PAN Number'}
                </Button>
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Tax Rules Box */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={getConfigurationFinanceCardSx(isDark, isMobileLayout)}
          >
            <Box sx={getConfigurationEmbeddedSectionHeaderSx(isMobileLayout)}>
              <Box sx={getConfigurationEmbeddedSectionLeadSx(isMobileLayout)}>
                <Box sx={getConfigurationEmbeddedIconBoxSx('#4ce1b6', isMobileLayout)}>
                  <ReceiptIcon sx={{ fontSize: isMobileLayout ? 26 : 32, color: '#4ce1b6' }} />
                </Box>
                <Box>
                  <Typography sx={getConfigurationEmbeddedSectionTitleSx(isMobileLayout)}>
                    Tax Rules
                  </Typography>
                  <Typography sx={getConfigurationEmbeddedSectionSubtitleSx(isMobileLayout)}>
                    Added rules, grouped by applicable on
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenTaxDrawer()}
                sx={getConfigurationAddButtonSx('#4ce1b6', isMobileLayout)}
              >
                Add Tax Rule
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <FinanceTaxRulesBoard
                taxRules={taxRules}
                currencyOptions={CURRENCY_OPTIONS}
                isDark={isDark}
                onEdit={handleOpenTaxDrawer}
                onDelete={handleDeleteTaxRule}
                onAdd={(applicableOn) => handleOpenTaxDrawer(null, { applicable_on: applicableOn })}
              />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tax Rule Drawer — layout matches Room Type drawer */}
      <Drawer
        anchor="right"
        open={openTaxDrawer}
        onClose={handleCloseTaxDrawer}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon sx={{ color: '#4ce1b6', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? '#ddd' : '#333' }}>
                {editingTaxRule ? 'Edit Tax Rule' : 'Add Tax Rule'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              onClick={handleCloseTaxDrawer}
              disabled={taxRuleSaving}
              sx={{
                borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                color: '#4ce1b6',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1.5,
                py: 0.3,
                minWidth: 84,
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                },
                '&.Mui-disabled': {
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#d0d0d0',
                  color: isDark ? '#666' : '#999',
                },
              }}
            >
              Close
            </Button>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <TextField
              label="Name"
              value={taxFormData.name}
              onChange={(e) => handleTaxInputChange('name', e.target.value)}
              error={!!taxErrors.name}
              helperText={taxErrors.name}
              required
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Tax Type</InputLabel>
              <Select
                value={taxFormData.tax_type}
                onChange={(e) => handleTaxInputChange('tax_type', e.target.value)}
                label="Tax Type"
                MenuProps={drawerOverlays.selectMenuProps}
              >
                {TAX_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Applicable On</InputLabel>
              <Select
                value={taxFormData.applicable_on}
                onChange={(e) => handleTaxInputChange('applicable_on', e.target.value)}
                label="Applicable On"
                MenuProps={drawerOverlays.selectMenuProps}
              >
                {APPLICABLE_ON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Tax Percentage (%)"
              type="number"
              value={taxFormData.tax_percentage}
              onChange={(e) => handleTaxInputChange('tax_percentage', e.target.value)}
              error={!!taxErrors.tax_percentage}
              helperText={taxErrors.tax_percentage}
              fullWidth
              disabled={!!taxFormData.tax_amount}
            />

            <TextField
              label="Tax Amount (Fixed)"
              type="number"
              value={taxFormData.tax_amount}
              onChange={(e) => handleTaxInputChange('tax_amount', e.target.value)}
              error={!!taxErrors.tax_amount}
              helperText={taxErrors.tax_amount}
              fullWidth
              disabled={!!taxFormData.tax_percentage}
            />

            <TextField
              label="Minimum Amount"
              type="number"
              value={taxFormData.min_amount}
              onChange={(e) => handleTaxInputChange('min_amount', e.target.value)}
              error={!!taxErrors.min_amount}
              helperText={taxErrors.min_amount}
              fullWidth
            />

            <TextField
              label="Maximum Amount"
              type="number"
              value={taxFormData.max_amount}
              onChange={(e) => handleTaxInputChange('max_amount', e.target.value)}
              error={!!taxErrors.max_amount}
              helperText={taxErrors.max_amount}
              fullWidth
            />

            <Autocomplete
              options={CURRENCY_OPTIONS}
              getOptionLabel={(option) => typeof option === 'string' 
                ? option 
                : `${option.code} - ${option.name} (${option.symbol})`}
              value={CURRENCY_OPTIONS.find(c => c.code === taxFormData.currency) || null}
              onChange={(event, newValue) => {
                handleTaxInputChange('currency', newValue ? newValue.code : '');
              }}
              slotProps={drawerOverlays.autocompleteSlotProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Currency"
                  placeholder="Search currency..."
                  fullWidth
                />
              )}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.code.toLowerCase().includes(searchTerm) ||
                  option.name.toLowerCase().includes(searchTerm) ||
                  option.symbol.toLowerCase().includes(searchTerm)
                );
              }}
              isOptionEqualToValue={(option, value) => option.code === value.code}
            />

            <TextField
              label="Description"
              value={taxFormData.description}
              onChange={(e) => handleTaxInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Sort Order"
              type="number"
              value={taxFormData.sort_order}
              onChange={(e) => handleTaxInputChange('sort_order', parseInt(e.target.value) || 0)}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={taxFormData.is_active}
                  onChange={(e) => handleTaxInputChange('is_active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseTaxDrawer}
                  disabled={taxRuleSaving}
                  sx={{
                    borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#ddd' : '#666',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#4ce1b6',
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: '#4ce1b6',
                    },
                    '&:disabled': {
                      borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                      color: isDark ? '#666' : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveTaxRule}
                  disabled={taxRuleSaving}
                  startIcon={taxRuleSaving ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{
                    backgroundColor: '#4ce1b6',
                    color: '#000',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#4ce1b6',
                      opacity: 0.9,
                      boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                      color: isDark ? '#999' : '#666',
                    },
                  }}
                >
                  {editingTaxRule ? 'Update' : 'Create'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tax Rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the tax rule "{taxRuleToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteTaxConfirm}
            color="error"
            variant="contained"
            disabled={deletingId === taxRuleToDelete?.id}
          >
            {deletingId === taxRuleToDelete?.id ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinanceSetupPage;
